import { useCallback } from 'react';
import { observable, runInAction } from 'mobx';
import $auth from 'app/stores/$auth';

/**
 * Optimistic override shared across consumers so the toggle reflects instantly
 * while the backend persist request is in flight. Null means "use stored value".
 */
const optimisticValue = observable.box<boolean | null>(null);

/**
 * Debounce the persist call so rapid toggles only send the final value (switchMap-like).
 * The `api` layer doesn't expose request abort, so a stale request that resolves late
 * only clears the override when the optimistic value still matches what it persisted.
 */
let persistTimer: ReturnType<typeof setTimeout> | undefined;
const PERSIST_DEBOUNCE_MS = 400;

export function useAutoApply(): [boolean, (value: boolean) => void] {
  const stored: boolean = $auth.userSettings.udeAutoApply ?? true;
  const autoApply = optimisticValue.get() ?? stored;

  const setAutoApply = useCallback((value: boolean) => {
    runInAction(() => optimisticValue.set(value));

    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      Promise.resolve($auth.setUserSettings({ udeAutoApply: value }))
        .catch(() => {})
        .finally(() => {
          if (optimisticValue.get() === value) {
            runInAction(() => optimisticValue.set(null));
          }
        });
    }, PERSIST_DEBOUNCE_MS);
  }, []);

  return [autoApply, setAutoApply];
}
